import type { SheetsClient } from '../client';
import type { BookRow, BookType } from '../types';
import {
  appendRow,
  genericSoftDelete,
  genericUpdate,
  newRowDefaults,
  readAllRows,
  type ListOptions,
} from './_base';
import { parseBool, parseInt10 } from '../row-utils';

const TAB = 'books' as const;

const BOOK_TYPES: ReadonlySet<BookType> = new Set([
  'personal',
  'family',
  'project',
]);

function coerceBookType(v: string | undefined): BookType {
  // Lazy-dev guard: any unrecognised / empty `type` cell falls back to
  // 'personal' rather than crashing the read. The migration backfills
  // empty cells, but we still want resilience for hand-edited sheets.
  if (v && BOOK_TYPES.has(v as BookType)) return v as BookType;
  return 'personal';
}

function parse(o: Record<string, string>): BookRow {
  return {
    id: o['id'] ?? '',
    name: o['name'] ?? '',
    color_token: o['color_token'] ?? '',
    type: coerceBookType(o['type']),
    is_default: parseBool(o['is_default']),
    rev: parseInt10(o['rev'], 0),
    created_at: o['created_at'] ?? '',
    updated_at: o['updated_at'] ?? '',
    deleted_at: (o['deleted_at'] ?? '') as BookRow['deleted_at'],
  };
}

export const booksRepo = {
  async list(
    client: SheetsClient,
    spreadsheetId: string,
    options?: ListOptions,
  ): Promise<BookRow[]> {
    const rows = await readAllRows(client, spreadsheetId, TAB, options);
    const out: BookRow[] = [];
    for (const r of rows) {
      try {
        out.push(parse(r));
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('Skipping malformed book row', { id: r['id'], err });
      }
    }
    return out;
  },

  async get(
    client: SheetsClient,
    spreadsheetId: string,
    id: string,
  ): Promise<BookRow | null> {
    const all = await readAllRows(client, spreadsheetId, TAB, { includeDeleted: true });
    const row = all.find((r) => r['id'] === id);
    return row ? parse(row) : null;
  },

  async create(
    client: SheetsClient,
    spreadsheetId: string,
    input: Partial<BookRow> & { id?: string },
  ): Promise<BookRow> {
    const row = newRowDefaults({
      id: input.id,
      name: input.name ?? '',
      color_token: input.color_token ?? '--terracotta-500',
      type: coerceBookType(input.type as string | undefined),
      is_default: input.is_default ?? false,
    });
    await appendRow(client, spreadsheetId, TAB, row);
    return parse(row);
  },

  update(
    client: SheetsClient,
    spreadsheetId: string,
    id: string,
    patch: Partial<BookRow>,
    expectedRev: number,
  ) {
    return genericUpdate(client, spreadsheetId, TAB, id, patch as Record<string, unknown>, expectedRev);
  },

  softDelete(
    client: SheetsClient,
    spreadsheetId: string,
    id: string,
    expectedRev: number,
  ) {
    return genericSoftDelete(client, spreadsheetId, TAB, id, expectedRev);
  },
};
